/**
 * applySemanticImport — the shared runtime consumer for any semantic import.
 *
 * Given a SemanticSnapshot (built by a reference-dialect or canvas builder),
 * it creates platform items that carry meaning: content + category + external
 * identity (the set-3 spine) + canonical relationships, grouped into a
 * Collection. This is the import-time analog of excursionApply's reconcile —
 * merge-less, since a fresh import has no baseline.
 *
 * Uncertainty policy (02-DESIGN §5): explicit / symbol / visual_language →
 * write the edge directly; spatial / llm_inference → Nexus proposal (never a
 * silent write). §2.3a: no authority-lane transition.
 *
 * Notion and Linear both call this; the only per-dialect input is the snapshot
 * and a few labels — the runtime is one path.
 */

import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore';
import { useAuditStore } from '../../stores/prioritization/auditStore';
import { createNexusProposal } from '../nexusAgentService';
import type { ItemLanguageType } from '../../types/prioritization/lifecycle';
import type { SemanticSnapshot } from './snapshot';
import { registerImportedRecords, loadImportedRecordMappings, type ImportedRecordIdentity } from './importIdentity';
import { buildPriorImportIndex, externalIdsAlreadyImported, newImportBatchId } from '../importBatch';
import { recordAndPropagateInbound } from './unifiedAttributesService';

const DIRECT_WRITE_BASES = new Set(['explicit', 'symbol', 'visual_language']);

function proposalConfidence(confidence: number): 'high' | 'medium' | 'low' {
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.6) return 'medium';
  return 'low';
}

export interface SemanticImportResult {
  itemsCreated: number;
  /** Records skipped because an earlier, un-retired import already created them. */
  itemsSkipped: number;
  /** Ids of the items this run created (the batch), for "View" / "Undo". */
  createdItemIds: string[];
  /** The item each created record became, by the record's external id. */
  createdByExternalId: Record<string, string>;
  /** The import batch id stamped on every created item (items.import_batch_id). */
  importBatchId: string;
  /** Edges this run wrote, so an undo can dissolve them before retiring. */
  createdEdgeIds: string[];
  edgesCreated: number;
  proposalsCreated: number;
  collectionId?: string;
  errors: string[];
}

export interface SemanticImportContext {
  workspaceId: string;
  /** ConnectorType — stamped on identity, change events, provenance. */
  connectorType: string;
  /** Human source label, e.g. 'Notion import' / 'Linear import'. */
  sourceLabel: string;
  /** Tag applied to created items, e.g. 'notion-import'. */
  sourceTag: string;
  /** Nexus proposal agent id, e.g. 'notion_import'. */
  proposedByAgent: string;
  /** Container title for the Collection + provenance note. */
  containerTitle: string;
  /** External deep-link per object, for identity registration. */
  externalUrlByExternalId?: Map<string, string | undefined>;
  userId?: string;
  groupIntoCollection?: boolean;
  /** One id per import run; minted here when the caller doesn't supply one. */
  importBatchId?: string;
}

export async function applySemanticImport(
  snapshot: SemanticSnapshot,
  ctx: SemanticImportContext,
): Promise<SemanticImportResult> {
  const { workspaceId, connectorType, sourceLabel, sourceTag, proposedByAgent, containerTitle, userId } = ctx;
  const groupIntoCollection = ctx.groupIntoCollection ?? true;

  const importBatchId = ctx.importBatchId ?? newImportBatchId();
  const result: SemanticImportResult = {
    itemsCreated: 0,
    itemsSkipped: 0,
    createdItemIds: [],
    createdByExternalId: {},
    importBatchId,
    createdEdgeIds: [],
    edgesCreated: 0,
    proposalsCreated: 0,
    errors: [],
  };
  const itemsStore = useItemsStore.getState();

  // Duplicate-skip on retry: a record whose mapped item came from an earlier,
  // un-retired import batch is not created again. Its existing item still
  // resolves relations below, so re-running an import never orphans edges.
  const priorIndex = buildPriorImportIndex(itemsStore.items);
  const mappings = priorIndex.itemIds.size > 0
    ? await loadImportedRecordMappings({
        workspaceId,
        connectorType,
        externalIds: snapshot.objects.map((o) => o.externalId),
      })
    : [];
  const alreadyImported = externalIdsAlreadyImported(priorIndex, mappings);
  const existingByExternal = new Map(mappings.map((m) => [m.externalId, m.internalId]));
  const relationshipsStore = useItemRelationshipsStore.getState();
  const logItemImport = useAuditStore.getState().logItemImport;

  // Create items; hold the external→internal id map for edge resolution.
  const internalByExternal = new Map<string, string>();
  const identities: ImportedRecordIdentity[] = [];
  // Items this run created (external id → item id); relations may also
  // resolve to skipped, previously imported items via internalByExternal.
  const createdByExternal = new Map<string, string>();
  for (const object of snapshot.objects) {
    if (alreadyImported.has(object.externalId)) {
      const existingId = existingByExternal.get(object.externalId);
      if (existingId) internalByExternal.set(object.externalId, existingId);
      result.itemsSkipped++;
      continue;
    }
    const itemId = crypto.randomUUID();
    const category = object.asserted.itemCategory;
    try {
      // Awaited so a refused insert is not counted as created (the old
      // fire-and-forget call reported every row as a success).
      const ok = await itemsStore.createItem({
        id: itemId,
        title: object.content.text,
        description: object.content.description ?? '',
        source: sourceLabel,
        tags: [sourceTag],
        creationSource: 'automated',
        creationReviewStatus: 'none',
        importBatchId,
        ...(category ? { itemCategory: category.value as ItemLanguageType } : {}),
      } as Parameters<typeof itemsStore.createItem>[0]);
      if (!ok) {
        result.errors.push(`Create failed for “${object.content.text}”: the item could not be saved`);
        continue;
      }
      internalByExternal.set(object.externalId, itemId);
      createdByExternal.set(object.externalId, itemId);
      result.createdItemIds.push(itemId);
      result.createdByExternalId[object.externalId] = itemId;
      identities.push({
        internalId: itemId,
        externalId: object.externalId,
        label: object.content.text,
        ...(ctx.externalUrlByExternalId?.get(object.externalId)
          ? { externalUrl: ctx.externalUrlByExternalId.get(object.externalId) }
          : {}),
      });
      result.itemsCreated++;
      logItemImport({
        type: 'item_imported',
        itemId,
        source: `${connectorType}_import`,
        actorId: userId ?? 'unknown',
        actorRole: 'creator',
        note: `Imported from ${sourceLabel} “${containerTitle}”${
          category ? ` — category ${category.value} (${category.basis}, ${Math.round(category.confidence * 100)}%)` : ''
        }.`,
        details: { connectorType, externalId: object.externalId },
      });
    } catch (err) {
      result.errors.push(`Create failed for “${object.content.text}”: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  // Register identity + change events onto the sync fabric (set 3 primitive).
  if (identities.length > 0) {
    const reg = await registerImportedRecords({ workspaceId, entityType: 'item', connectorType, records: identities });
    result.errors.push(...reg.failures);
  }

  // Unified attributes (doc 07): record what the dialect said about each item
  // at rest, and apply any bound canonical values. Never blocks the import.
  if (createdByExternal.size > 0) {
    const crossing = await recordAndPropagateInbound({
      workspaceId,
      connectorType,
      origin: 'import',
      objects: snapshot.objects
        .filter((o) => createdByExternal.has(o.externalId))
        .map((o) => ({ object: o, itemId: createdByExternal.get(o.externalId)! })),
      userId,
    });
    result.errors.push(...crossing.errors);
  }

  // Relations → edges (direct) or proposals (inferred), per the uncertainty policy.
  const existingEdges = useItemRelationshipsStore.getState().edges;
  for (const relation of snapshot.relations) {
    const sourceId = internalByExternal.get(relation.sourceExternalId);
    const targetId = internalByExternal.get(relation.targetExternalId);
    if (!sourceId || !targetId || sourceId === targetId) continue;

    if (!DIRECT_WRITE_BASES.has(relation.assertion.basis)) {
      const { error } = await createNexusProposal({
        workspaceId,
        sourceItemId: sourceId,
        targetItemId: targetId,
        relationshipType: relation.canonicalType,
        relationshipSubtype: relation.subtype,
        directionality: relation.directionality,
        rationale: `Inferred from ${sourceLabel} (${relation.assertion.basis}, ${Math.round(relation.assertion.confidence * 100)}%): ${relation.cue ?? 'relation'}.`,
        confidence: proposalConfidence(relation.assertion.confidence),
        proposedByAgent,
      });
      if (error) result.errors.push(`Proposal failed (${relation.canonicalType}): ${error}`);
      else result.proposalsCreated++;
      continue;
    }

    const duplicate = existingEdges.some(
      e =>
        e.type === relation.canonicalType &&
        ((e.sourceItemId === sourceId && e.targetItemId === targetId) ||
          (relation.directionality === 'undirected' && e.sourceItemId === targetId && e.targetItemId === sourceId)),
    );
    if (duplicate) continue;
    try {
      const edge = await relationshipsStore.createEdge({
        type: relation.canonicalType,
        ...(relation.subtype ? { subtype: relation.subtype as never } : {}),
        directionality: relation.directionality,
        status: 'confirmed',
        declaredMode: 'emergent',
        sourceItemId: sourceId,
        targetItemId: targetId,
        rationale: `From ${sourceLabel} relation (${relation.cue ?? relation.assertion.basis}, ${Math.round(relation.assertion.confidence * 100)}%).`,
        metadata: { source: `${connectorType}_import`, basis: relation.assertion.basis },
        ...(workspaceId ? { workspaceId } : {}),
        ...(userId ? { createdBy: userId } : {}),
      });
      if (edge) {
        result.edgesCreated++;
        result.createdEdgeIds.push(edge.id);
      }
    } catch (err) {
      result.errors.push(`Edge write failed (${relation.canonicalType}): ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  // Group the import into a named Collection so it stays navigable.
  if (groupIntoCollection && createdByExternal.size > 0) {
    try {
      const group = await relationshipsStore.createGroup({
        type: 'collection',
        subtype: 'curation',
        status: 'confirmed',
        declaredMode: 'declared',
        title: containerTitle || sourceLabel,
        note: `Imported from ${sourceLabel}`,
        rationale: `Imported together from one ${connectorType} source`,
        memberItemIds: Array.from(createdByExternal.values()),
        ...(userId && { createdBy: userId }),
      });
      if (group) result.collectionId = group.id;
    } catch {
      /* collection is best-effort; items + edges already landed */
    }
  }

  return result;
}
